import { AnimatePresence, motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import {
    FaChartLine,
    FaCoins,
    FaDatabase,
    FaFire,
    FaGem,
    FaLayerGroup,
    FaLock,
    FaShieldAlt,
    FaUsers,
    FaVoteYea
} from "react-icons/fa";
import {
    TiArrowRight,
    TiArrowUp,
    TiChartPie,
    TiFlash,
    TiFlashOutline,
    TiLockClosed,
    TiTime
} from "react-icons/ti";
import { useNavigate } from "react-router-dom";
import AnimatedTitle from "./AnimatedTitle";
import Footer from "./Footer";
import ScrollDownButton from './ScrollDownButton';
import GlassButton from './ui/GlassButton';
import { SparklesCore } from './ui/SparklesCore';

gsap.registerPlugin(ScrollTrigger);

const Tokenomics = () => {
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);
  const [activeAllocation, setActiveAllocation] = useState(null);
  const navigate = useNavigate();

  const sectionRefs = useRef([]);
  const scrollDownRef = useRef(null);
  const scrollTopRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            delay: index * 0.1,
            scrollTrigger: {
              trigger: section,
              start: "top 85%",
            },
          }
        );
      }
    });

    if (scrollDownRef.current) {
      gsap.fromTo(
        scrollDownRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 1 }
      );
    }
  }, []);

  const checkIfBottom = () => {
    if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 200) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const scrollToContent = () => {
    const content = document.getElementById("content");
    if (content) {
      const contentOffsetTop = content.offsetTop;
      window.scrollTo({
        top: contentOffsetTop - 100,
        behavior: "smooth",
      });
    }
    setScrollDownClicked(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false);
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  const stats = [
    { value: "1,000,000,000", label: "Total Supply", icon: <FaCoins className="text-cyan-400" /> },
    { value: "2.5%", label: "Annual Burn Rate", icon: <FaFire className="text-orange-400" /> },
    { value: "15%", label: "Staking APY", icon: <FaGem className="text-purple-400" /> },
    { value: "1M+", label: "Target Holders", icon: <FaUsers className="text-emerald-400" /> },
  ];

  const tokenAllocation = [
    { name: "Public Sale", percentage: 40, color: "from-cyan-400 to-cyan-600", colorCode: "#22d3ee", description: "Largest allocation dedicated to community ownership and market liquidity.", status: "Active" },
    { name: "Ecosystem & Rewards", percentage: 20, color: "from-purple-400 to-purple-600", colorCode: "#a855f7", description: "Incentives for staking, DAO participation, and node operators.", status: "Locked" },
    { name: "Team & Advisors", percentage: 15, color: "from-orange-400 to-orange-600", colorCode: "#f97316", description: "Subject to 24-month linear vesting with 6-month cliff.", status: "Vesting" },
    { name: "Development", percentage: 10, color: "from-emerald-400 to-emerald-600", colorCode: "#10b981", description: "Foundation treasury for continuous protocol engineering.", status: "Locked" },
    { name: "Marketing", percentage: 8, color: "from-pink-400 to-pink-600", colorCode: "#ec4899", description: "Global adoption campaigns and strategic partnership funding.", status: "Active" },
    { name: "Reserve", percentage: 7, color: "from-blue-400 to-blue-600", colorCode: "#6366f1", description: "Strategic rainy-day fund for extreme market volatility.", status: "Locked" },
  ];

  const utilities = [
    {
      title: "Governance Voting",
      description: "Direct control over protocol upgrades, treasury allocation, and future product listings through the Zero-DAO.",
      icon: <FaVoteYea />,
      color: "cyan"
    },
    {
      title: "Yield Generation",
      description: "Stake tokens in multi-tier liquidity pools to earn up to 15% APY and rewards from cross-chain transaction fees.",
      icon: <TiFlash />,
      color: "purple"
    },
    {
      title: "Deflationary Burn",
      description: "2.5% of all ecosystem transaction fees are permanently burned, reducing supply and increasing scarcity over time.",
      icon: <FaFire />,
      color: "orange"
    },
    {
      title: "Institutional Access",
      description: "Unlock advanced analytics, API keys, and professional-grade trading tools across the entire Zero-Banking suite.",
      icon: <FaDatabase />,
      color: "emerald"
    }
  ];

  return (
    <div className="w-screen min-h-screen bg-black text-blue-50">
      {/* Hero Section */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
        <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 bg-black shadow-[0_0_80px_rgba(34,211,238,0.15)]">
          <video
            src="/videos/tokenomics-hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <SparklesCore
              id="tokenomicsHeroSparkles"
              background="transparent"
              minSize={0.6}
              maxSize={1.4}
              particleDensity={50}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
            <AnimatedTitle
              title="The Economy of Zero"
              containerClass="text-center text-blue-50 font-zentry relative z-10"
            />
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
            >
              Deflationary · Scalable · Multi-Chain
            </motion.p>
          </div>
        </div>
      </div>

      {/* Scroll Down Button */}
      {!scrollDownClicked && (
        <ScrollDownButton
          ref={scrollDownRef}
          onClick={scrollToContent}
        />
      )}

      {/* Content Section */}
      <div id="content" className="px-6 md:px-10 py-32 mx-auto max-w-7xl">
        
        {/* Core Narrative & Stats */}
        <div className="grid lg:grid-cols-2 gap-20 mb-48 items-center" ref={(el) => (sectionRefs.current[0] = el)}>
          <div className="relative">
            <div className="absolute -top-20 -left-20 w-64 h-64 bg-cyan-500/10 rounded-full blur-[100px]" />
            <div className="flex items-center gap-3 text-cyan-400 font-bold uppercase tracking-[0.3em] text-[10px] mb-8">
              <span className="w-12 h-px bg-cyan-400"></span>
              Economic Protocol
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-10 leading-[1.15]">
              Designed for <span className="text-cyan-400 italic font-zentry">Infinite Stability</span>
            </h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-10">
              Zero Token (ZERO) is the native fuel of the Zero-Blockchain ecosystem. 
              Our tokenomics model prioritises long-term sustainability through a controlled 
              burn mechanism, institutional-grade staking, and community-led governance.
            </p>
            
            <div className="grid grid-cols-2 gap-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="p-6 bg-white/5 border border-white/10 rounded-3xl hover:border-cyan-500/40 transition-all group"
                >
                  <div className="text-2xl mb-3 group-hover:scale-110 transition-transform">{stat.icon}</div>
                  <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
          
          <div className="relative aspect-square">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-full blur-[120px] animate-pulse" />
            <div className="relative h-full w-full border border-white/10 rounded-[4rem] bg-black/40 backdrop-blur-xl p-12 flex items-center justify-center overflow-hidden">
               {/* Visual Token Representation */}
               <motion.div 
                 animate={{ rotate: 360 }}
                 transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                 className="relative w-full h-full flex items-center justify-center"
               >
                 <div className="absolute inset-0 border-[20px] border-dashed border-cyan-500/10 rounded-full" />
                 <div className="absolute w-[80%] h-[80%] border-[2px] border-purple-500/20 rounded-full" />
                 <div className="absolute w-[60%] h-[60%] border-[1px] border-white/10 rounded-full" />
               </motion.div>
               
               <div className="absolute flex flex-col items-center">
                 <FaGem className="text-8xl text-cyan-400 mb-6 drop-shadow-[0_0_30px_rgba(34,211,238,0.5)]" />
                 <span className="text-5xl font-zentry italic text-white uppercase tracking-tighter">Zero</span>
                 <span className="text-[10px] text-gray-300 uppercase tracking-[0.5em] mt-2">The Standard</span>
               </div>
            </div>
          </div>
        </div>

        {/* Global Allocation Bento */}
        <div className="mb-48" ref={(el) => (sectionRefs.current[1] = el)}>
          <div className="text-center mb-24">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">Strategic Allocation</h3>
            <p className="text-gray-300 max-w-2xl mx-auto">Our distribution model ensures decentralised ownership while funding core protocol development and ecosystem growth.</p>
          </div>

          <div className="grid lg:grid-cols-12 gap-8 items-stretch">
            {/* Legend & Details (Left col) */}
            <div className="lg:col-span-4 space-y-4">
              {tokenAllocation.map((item, index) => (
                <motion.div
                  key={index}
                  onMouseEnter={() => setActiveAllocation(index)}
                  onMouseLeave={() => setActiveAllocation(null)}
                  whileHover={{ x: 10 }}
                  className={`p-6 rounded-3xl border transition-all cursor-pointer ${
                    activeAllocation === index 
                    ? 'bg-white/10 border-white/20' 
                    : 'bg-white/5 border-white/5'
                  }`}
                >
                   <div className="flex justify-between items-center mb-2">
                     <span className="flex items-center gap-2 text-sm font-bold text-white">
                        <span className={`w-3 h-3 rounded-full bg-gradient-to-br ${item.color}`} />
                        {item.name}
                     </span>
                     <span className="text-xl font-zentry text-white">{item.percentage}%</span>
                   </div>
                   <div className="flex gap-2">
                     <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/10 text-gray-300 border border-white/5">
                       {item.status}
                     </span>
                   </div>
                </motion.div>
              ))}
            </div>

            {/* Visual Display (Right col) */}
            <div className="lg:col-span-8 relative rounded-[3rem] border border-white/10 bg-black/60 overflow-hidden min-h-[500px] flex items-center justify-center">
                <AnimatePresence mode="wait">
                  {activeAllocation !== null ? (
                    <motion.div
                      key={activeAllocation}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 1.05 }}
                      className="absolute inset-0 p-16 flex flex-col justify-center text-center z-10"
                    >
                      <div className={`text-7xl font-zentry italic mb-8 bg-gradient-to-br ${tokenAllocation[activeAllocation].color} bg-clip-text text-transparent`}>
                        {tokenAllocation[activeAllocation].percentage}%
                      </div>
                      <h4 className="text-3xl font-bold text-white mb-6 uppercase tracking-wider">{tokenAllocation[activeAllocation].name}</h4>
                      <p className="text-gray-300 text-lg leading-relaxed max-w-lg mx-auto">
                        {tokenAllocation[activeAllocation].description}
                      </p>
                    </motion.div>
                  ) : (
                    <motion.div 
                      key="default"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex flex-col items-center gap-8"
                    >
                       <div className="relative w-64 h-64">
                          <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                            {tokenAllocation.reduce((acc, curr, i) => {
                              const offset = acc.total;
                              acc.total += curr.percentage;
                              acc.elements.push(
                                <motion.circle
                                  key={i}
                                  cx="50"
                                  cy="50"
                                  r="40"
                                  stroke={curr.colorCode}
                                  strokeWidth="12"
                                  strokeDasharray={`${curr.percentage} ${100 - curr.percentage}`}
                                  strokeDashoffset={-offset}
                                  fill="transparent"
                                  initial={{ pathLength: 0 }}
                                  animate={{ pathLength: 1 }}
                                  transition={{ duration: 1, delay: i * 0.1 }}
                                  className="opacity-80 hover:opacity-100 transition-opacity"
                                />
                              );
                              return acc;
                            }, { total: 0, elements: [] }).elements}
                            <circle cx="50" cy="50" r="30" className="fill-black" />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-2xl font-zentry italic text-white">1B</span>
                          </div>
                       </div>
                       <p className="text-gray-500 uppercase tracking-widest text-[10px] font-bold">Hover sectors to explore</p>
                    </motion.div>
                  )}
                </AnimatePresence>
                
                {/* Background Grid Pattern */}
                <div className="absolute inset-0 opacity-10 pointer-events-none" 
                     style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
            </div>
          </div>
        </div>

        {/* Utility Ecosystem */}
        <div className="mb-48" ref={(el) => (sectionRefs.current[2] = el)}>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
             {utilities.map((util, index) => (
                <motion.div
                  key={index}
                  whileHover={{ y: -10 }}
                  className="p-10 bg-white/5 border border-white/10 rounded-[3.5rem] relative overflow-hidden group"
                >
                  <div className={`absolute -top-10 -right-10 w-32 h-32 bg-${util.color}-500/10 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity`} />
                  
                  <div className={`w-16 h-16 rounded-3xl bg-${util.color}-500/10 flex items-center justify-center text-3xl text-${util.color}-400 mb-10 border border-white/5`}>
                    {util.icon}
                  </div>
                  
                  <h4 className="text-2xl font-bold text-white mb-6 leading-tight group-hover:text-cyan-400 transition-colors">{util.title}</h4>
                  <p className="text-gray-300 text-sm leading-relaxed">{util.description}</p>
                </motion.div>
             ))}
          </div>
        </div>

        {/* Vesting Roadmap */}
        <div className="mb-48 p-12 md:p-20 bg-white/5 border border-white/10 rounded-[4rem] relative overflow-hidden" ref={(el) => (sectionRefs.current[3] = el)}>
           <div className="absolute top-0 right-0 p-10 opacity-5 text-[15rem] pointer-events-none">
              <TiLockClosed />
           </div>
           <div className="grid lg:grid-cols-2 gap-20 items-center relative z-10">
              <div>
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-8">Responsible <span className="text-cyan-400 italic font-zentry">Capital Management</span></h3>
                <p className="text-gray-300 mb-10 leading-relaxed">
                  To ensure the long-term commitment of our early contributors, team, and advisors, 
                  all non-public allocations are subject to a rigorous linear vesting schedule 
                  executed via immutable smart contracts.
                </p>
                <div className="space-y-6">
                  <div className="flex gap-6 items-start">
                    <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 flex items-center justify-center text-cyan-400 shrink-0 border border-white/5"><TiChartPie /></div>
                    <div>
                      <div className="text-white font-bold text-lg mb-1">6-Month Cliff</div>
                      <div className="text-gray-500 text-sm">No tokens released to core members for the first 180 days post-launch.</div>
                    </div>
                  </div>
                  <div className="flex gap-6 items-start">
                    <div className="w-12 h-12 rounded-2xl bg-purple-500/10 flex items-center justify-center text-purple-400 shrink-0 border border-white/5"><TiTime /></div>
                    <div>
                      <div className="text-white font-bold text-lg mb-1">24-Month Linear Vesting</div>
                      <div className="text-gray-500 text-sm">Gradual daily release of vested tokens over the subsequent two years.</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-black/60 rounded-3xl p-8 border border-white/10">
                <h4 className="text-white font-bold mb-8 flex items-center gap-2"><FaChartLine className="text-emerald-400" /> Projected Circulating Supply</h4>
                <div className="flex items-end gap-1 h-48 w-full">
                   {[30, 35, 45, 52, 65, 75, 85, 95, 100].map((h, i) => (
                     <div key={i} className="flex-1 bg-gradient-to-t from-cyan-500/50 to-cyan-500/10 rounded-t-lg transition-all hover:scale-x-110" style={{ height: `${h}%` }} />
                   ))}
                </div>
                <div className="flex justify-between mt-4 text-[10px] text-gray-500 uppercase tracking-widest font-bold">
                   <span>Launch</span>
                   <span>Year 1</span>
                   <span>Year 2</span>
                   <span>Final Cap</span>
                </div>
              </div>
           </div>
        </div>

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="relative rounded-[4rem] overflow-hidden border border-white/10 p-1"
          ref={(el) => (sectionRefs.current[4] = el)}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 via-purple-500/30 to-transparent" />
          <div className="absolute inset-0 bg-black/80 backdrop-blur-3xl" />
          
          <div className="relative z-10 py-32 px-8 text-center max-w-3xl mx-auto">
            <div className="w-24 h-24 rounded-[2rem] bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-5xl text-cyan-400 mx-auto mb-12 shadow-[0_0_50px_rgba(34,211,238,0.2)]">
              <FaLayerGroup />
            </div>
            <h3 className="text-4xl md:text-5xl font-bold text-white mb-10 leading-tight">
              Ready to Secure your <span className="font-zentry italic text-cyan-400">Digital Stake?</span>
            </h3>
            <p className="text-gray-300 mb-16 text-lg leading-relaxed">
              Don't miss the opportunity to hold the baseline currency of the next evolutionary leap in finance. 
              The private sale is capped, and the pre-sale is moving fast. 
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <GlassButton
                onClick={() => navigate('/ico')}
                variant="cyan"
                size="lg"
                className="px-8 py-6"
              >
                Buy Zero Tokens <TiArrowRight className="text-2xl" />
              </GlassButton>
              
              <GlassButton
                onClick={() => window.open('/Whitepapers/4-Zero-Tokens Whitepaper.docx')}
                variant="gray"
                size="lg"
                className="px-8 py-6"
              >
                View Whitepaper <TiFlashOutline className="text-2xl" />
              </GlassButton>
            </div>

            <div className="mt-20 flex flex-wrap justify-center gap-10 opacity-20 grayscale hover:grayscale-0 transition-all duration-700">
              <div className="flex items-center gap-2 font-bold tracking-widest text-[10px]"><FaShieldAlt /> ERC-20 COMPLIANT</div>
              <div className="flex items-center gap-2 font-bold tracking-widest text-[10px]"><FaLock /> MULTI-SIG TREASURY</div>
              <div className="flex items-center gap-2 font-bold tracking-widest text-[10px]"><FaFire /> DEFLATIONARY MODEL</div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Back to Top */}
      {isBottom && (
        <div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-cyan-500/30 rounded-full shadow-lg shadow-cyan-500/10 cursor-pointer hover:bg-black/60 hover:border-cyan-400/50 transition-all duration-500 z-50"
          onClick={scrollToTop}
        >
          <TiArrowUp className="text-cyan-400 transition-all duration-500" size={24} />
          <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider">BACK TO TOP</span>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Tokenomics;
